// Standard error surface for failed queries. Replaces the per-page
// `ErrorCard` ad-hoc shapes with a single component that knows how to
// extract `status`/`code` from `ApiError` and offers a retry button.

import { AlertCircle, RotateCcw } from 'lucide-react';
import { ApiError } from '../../lib/api-client.js';
import { Button } from './button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './card.js';

interface ErrorRetryProps {
  title?: string;
  error: unknown;
  onRetry?: () => void;
  className?: string;
}

export function ErrorRetry({ title = 'Could not load', error, onRetry, className }: ErrorRetryProps) {
  const status = error instanceof ApiError ? error.status : undefined;
  const code = error instanceof ApiError ? error.code : undefined;
  const message = error instanceof Error ? error.message : 'unknown error';

  return (
    <Card className={className} role="alert">
      <CardHeader className="flex flex-row items-start gap-3 space-y-0">
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive"
          aria-hidden
        >
          <AlertCircle className="h-5 w-5" />
        </div>
        <div className="space-y-1">
          <CardTitle>{title}</CardTitle>
          <CardDescription>
            {status !== undefined ? `HTTP ${String(status)}` : 'Network error'}
            {code ? ` · ${code}` : ''}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">{message}</p>
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RotateCcw className="h-4 w-4" aria-hidden />
            Try again
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
